import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import axios from 'axios';
import { LanguageSelector } from '../components/LanguageSelector';
import MenuItem from '../components/MenuItem';
import CartDrawer from '../components/CartDrawer';
import OrderStatusTracker from '../components/OrderStatusTracker';
import CustomerAuth from '../components/CustomerAuth';
import OrderHistory from '../components/OrderHistory';
import QuickDrinkButton, { DRINK_CATEGORIES } from '../components/QuickDrinkButton';
import { MapPin, Bell, Star, HandMetal } from 'lucide-react';
import { usePushNotifications } from '../hooks/usePushNotifications';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TablePage = () => {
  const { tableId } = useParams();
  const { t, language } = useLanguage();
  const { setTableId, setSessionId, sessionId } = useCart();
  const [menu, setMenu] = useState([]);
  const [orders, setOrders] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [showWelcome, setShowWelcome] = useState(true);
  const [categories, setCategories] = useState([]);
  const [categoryMap, setCategoryMap] = useState({});
  const [notification, setNotification] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [waiterCalled, setWaiterCalled] = useState(false);
  const categoryRef = useRef(null);
  const eventSourceRef = useRef(null);

  const { isSupported: pushSupported, isSubscribed: pushSubscribed, subscribe: pushSubscribe } = usePushNotifications('client', tableId);

  useEffect(() => {
    if (tableId) {
      setTableId(tableId);
      initSession();
      fetchMenu();
      fetchOrders();
      fetchCategories();
      connectSSE();
      restoreCustomer();
      fetchSuggestions();

      const timer = setTimeout(() => {
        setShowWelcome(false);
        // Show auth modal after welcome if no customer
        const savedCustomer = localStorage.getItem('hibisun_customer');
        if (!savedCustomer) {
          setTimeout(() => setShowAuth(true), 500);
        }
      }, 3000);
      return () => {
        clearTimeout(timer);
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
        }
      };
    }
  }, [tableId]);

  const restoreCustomer = () => {
    const saved = localStorage.getItem('hibisun_customer');
    if (saved) {
      try { setCustomer(JSON.parse(saved)); } catch (e) { /* ignore */ }
    }
  };

  const handleCustomerSet = (c) => {
    setCustomer(c);
    localStorage.setItem('hibisun_customer', JSON.stringify(c));
  };

  useEffect(() => {
    if (tableId) {
      fetchMenu();
      fetchCategories();
    }
  }, [language]);

  const connectSSE = () => {
    if (eventSourceRef.current) eventSourceRef.current.close();

    const es = new EventSource(`${API}/events/table/${tableId}`);
    eventSourceRef.current = es;

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'status_update') {
          fetchOrders();
          showNotification(data.status);
        } else if (data.type === 'new_order') {
          fetchOrders();
        }
      } catch (e) {
        // ignore parse errors on heartbeats
      }
    };

    es.onerror = () => {
      es.close();
      // Reconnect after 3 seconds
      setTimeout(() => {
        if (tableId) connectSSE();
      }, 3000);
    };
  };

  const showNotification = (status) => {
    const messages = {
      preparing: { text: t('preparing'), color: 'bg-[#E16833]' },
      ready: { text: t('ready'), color: 'bg-[#2D6A4F]' },
      served: { text: t('served'), color: 'bg-[#0A422D]' },
    };
    const msg = messages[status];
    if (!msg) return;
    setNotification(msg);
    // Play a subtle notification sound
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = status === 'ready' ? 880 : 660;
      gain.gain.value = 0.1;
      osc.start();
      osc.stop(ctx.currentTime + 0.2);
    } catch (e) { /* audio not available */ }
    setTimeout(() => setNotification(null), 4000);
  };

  const initSession = async () => {
    try {
      const response = await axios.post(`${API}/sessions`, {
        table_id: tableId,
        device_id: localStorage.getItem('deviceId') || `device_${Date.now()}`
      });
      setSessionId(response.data.id);
      localStorage.setItem('deviceId', response.data.device_id || `device_${Date.now()}`);
    } catch (error) {
      console.error('Error initializing session:', error);
    }
  };

  const fetchMenu = async () => {
    try {
      const response = await axios.get(`${API}/menu?lang=${language}`);
      setMenu(response.data);
    } catch (error) {
      console.error('Error fetching menu:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories?lang=${language}`);
      setCategories(['all', ...response.data.map(c => c.id)]);
      const map = { all: t('categories.all') || 'Tous' };
      response.data.forEach(c => { map[c.id] = c.display_name || c.name_fr || c.name; });
      setCategoryMap(map);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchOrders = async () => {
    if (!tableId) return;
    try {
      const response = await axios.get(`${API}/orders/table/${tableId}`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const fetchSuggestions = async () => {
    if (!tableId) return;
    try {
      const response = await axios.get(`${API}/suggestions/${tableId}`);
      setSuggestions(response.data.suggestions || []);
    } catch (error) { /* ignore */ }
  };

  const callWaiter = async () => {
    try {
      await axios.post(`${API}/call-waiter`, { table_id: tableId, message: "Appel serveur" });
      setWaiterCalled(true);
      setTimeout(() => setWaiterCalled(false), 10000);
    } catch (error) { console.error(error); }
  };

  const filteredMenu = activeCategory === 'all'
    ? menu.filter(item => item.category !== 'takeaway')
    : menu.filter(item => item.category === activeCategory);

  const popularItems = menu.filter(item => item.is_popular && item.category !== 'takeaway');

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#E16833] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#6E5E52]">Loading menu...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F2] pb-24">
      {/* Real-time notification toast */}
      {notification && (
        <div className={`fixed top-4 left-4 right-4 z-[60] ${notification.color} text-white px-4 py-3 rounded-2xl shadow-lg flex items-center gap-3 animate-slide-up`}
          data-testid="order-notification">
          <Bell className="w-5 h-5 flex-shrink-0" />
          <span className="font-semibold">{notification.text}</span>
        </div>
      )}

      {/* Welcome Overlay */}
      {showWelcome && (
        <div
          className="fixed inset-0 z-50 bg-[#0D0D0D] flex items-center justify-center animate-fade-in"
          onClick={() => setShowWelcome(false)}
          data-testid="welcome-overlay"
          style={{
            backgroundImage: 'url(https://d1yei2z3i6k35z.cloudfront.net/12255315/691ec46e53cfe_delicious_caribbean_food_in_patong_phuket_thailand.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0 bg-black/70" />
          <div className="text-center text-white p-8 relative z-10">
            <img
              src="https://d1yei2z3i6k35z.cloudfront.net/12255315/691ecb082b3bc_hibisun_caribbean_food_paton_phuket_thailand.png"
              alt="HIBISUN"
              className="w-24 h-24 mx-auto mb-6 rounded-full border-4 border-[#D4A853] shadow-xl"
            />
            <h1 className="text-4xl font-bold mb-2 gradient-text">{t('welcome')}</h1>
            <div className="flex items-center justify-center gap-2 text-[#D4A853]">
              <MapPin className="w-5 h-5" />
              <span className="text-xl">{t('table')} {tableId}</span>
            </div>
            <p className="text-white/60 mt-4 text-sm">Tap anywhere to continue</p>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#0D0D0D]/95 backdrop-blur-lg border-b border-[#333333]">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img
              src="https://d1yei2z3i6k35z.cloudfront.net/12255315/691ecb082b3bc_hibisun_caribbean_food_paton_phuket_thailand.png"
              alt="HIBISUN"
              className="w-10 h-10 rounded-full object-cover"
            />
            <div>
              <h1 className="font-bold text-[#D4A853] text-lg leading-tight">HIBISUN</h1>
              <p className="text-xs text-[#A0A0A0]">{t('table')} {tableId}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowAuth(true)}
              className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${customer ? 'bg-[#D4A853] text-black' : 'bg-[#1A1A1A] text-[#D4A853] border border-[#333333]'}`}
              data-testid="loyalty-btn">
              <Star className="w-4 h-4" />
            </button>
            {pushSupported && !pushSubscribed && (
              <button onClick={pushSubscribe}
                className="w-9 h-9 rounded-full flex items-center justify-center bg-[#1A1A1A] text-[#D4A853] border border-[#333333] hover:bg-[#D4A853] hover:text-black transition-colors"
                data-testid="push-subscribe-client">
                <Bell className="w-4 h-4" />
              </button>
            )}
            <LanguageSelector />
          </div>
        </div>

        {/* Category Pills */}
        <div
          ref={categoryRef}
          className="px-4 pb-3 flex gap-2 overflow-x-auto no-scrollbar category-scroll"
        >
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`category-pill ${activeCategory === category ? 'active' : ''}`}
              data-testid={`category-${category}`}
              data-cat={category}
            >
              {categoryMap[category] || category}
            </button>
          ))}
        </div>
      </header>

      {/* Order Status */}
      <div className="px-4 pt-4">
        <OrderStatusTracker orders={orders} />
      </div>

      {/* Order History (logged-in customers) */}
      {customer && (
        <OrderHistory customerId={customer.id} tableId={tableId}
          sessionId={sessionId} onReorder={() => fetchOrders()} />
      )}

      {/* Popular Items Section */}
      {activeCategory === 'all' && popularItems.length > 0 && (
        <section className="px-4 mb-6">
          <h2 className="text-lg font-bold text-[#D4A853] mb-3 flex items-center gap-2">
            <span className="text-xl">&#11088;</span> {t('popular')}
          </h2>
          <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2">
            {popularItems.map(item => (
              <div key={item.id} className="w-64 flex-shrink-0">
                <MenuItem item={item} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* AI Suggestions */}
      {suggestions.length > 0 && activeCategory === 'all' && (
        <section className="px-4 mb-6" data-testid="ai-suggestions">
          <h2 className="text-lg font-bold text-[#D4A853] mb-3 flex items-center gap-2">
            <Star className="w-5 h-5" /> Suggestions pour vous
          </h2>
          <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2">
            {suggestions.map(item => (
              <div key={item.id} className="w-40 flex-shrink-0">
                <MenuItem item={item} compact />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Menu Items */}
      <section className="px-4">
        <h2 className="text-lg font-bold text-white mb-3">
          {categoryMap[activeCategory] || activeCategory}
        </h2>
        <div className="space-y-3">
          {filteredMenu.map(item => (
            <MenuItem key={item.id} item={item} />
          ))}
        </div>
      </section>

      {/* Cart Drawer */}
      <CartDrawer orders={orders} customerId={customer?.id} />

      {/* Quick Drink Button */}
      <QuickDrinkButton onSelectCategory={(catId) => {
        setActiveCategory(catId);
        if (categoryRef.current) {
          const btn = categoryRef.current.querySelector(`[data-cat="${catId}"]`);
          if (btn) btn.scrollIntoView({ behavior: 'smooth', inline: 'center' });
        }
      }} />

      {/* Call Waiter Button */}
      <button
        onClick={callWaiter}
        disabled={waiterCalled}
        className={`fixed bottom-24 left-4 z-40 w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all ${waiterCalled ? 'bg-[#2D6A4F] scale-110' : 'bg-[#E16833] hover:bg-[#C95A2A] active:scale-95'}`}
        data-testid="call-waiter-btn"
      >
        {waiterCalled ? (
          <span className="text-white text-[10px] font-bold text-center leading-tight">En<br/>route</span>
        ) : (
          <HandMetal className="w-5 h-5 text-white" />
        )}
      </button>

      {/* Customer Auth / Loyalty Modal */}
      <CustomerAuth
        isOpen={showAuth}
        onClose={() => setShowAuth(false)}
        onCustomerSet={handleCustomerSet}
        customer={customer}
      />

      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default TablePage;
